import { useState } from "react"
import type { MetaFunction } from "react-router"
import { useSearchParams } from "react-router"

import { canonicalUrl, pageMeta, SITE_NAME, SITE_URL } from "~/lib/meta"

import { DocsBanner } from "~/components/landing/DocsBanner"
import { Button, ButtonLink } from "~/components/ui/button"
import { BlogLayout, BlogProse } from "~/components/blog/BlogLayout"
import { BlogCta } from "~/components/blog/BlogCta"
import { WrittenBy } from "~/components/blog/WrittenBy"
import { H2, List, P, Quote } from "~/components/blog/prose"
import { AUTHORS } from "~/data/authors"
import { getPost, postSocialImage } from "~/data/posts"

// First blog post: the launch announcement. The trailing underscore in the
// filename (blog_.) keeps this route a sibling of /blog rather than a child,
// so it renders standalone through <BlogLayout> like every other post.

const post = getPost("welcome-to-pine59")!

const PUBLISHED = post.published!
const MODIFIED = post.modified!

const POST_URL = canonicalUrl("/blog/welcome-to-pine59")
const POST_TITLE = "Welcome to Pine59"
const POST_SUMMARY = "A new name. A sharper focus. The same mission."

// This route can't use postMeta() (see the ?from-post note below) but still
// owes the post its own social preview.
const SOCIAL_IMAGE = postSocialImage(post)

// Old unacast.com blog posts land here with ?from-post=<slug>. The canonical
// rendered by root.tsx is pathname-only, so those parameterized URLs already
// collapse onto this post; og:url comes from the same helper.
export const meta: MetaFunction = ({ location }) =>
  pageMeta({
    title: "Welcome to Pine59 · Blog",
    description:
      "Introducing Pine59 - the new home of Unacast's former Aggregated Location Insights business. A new name, a sharper focus, the same mission.",
    ogTitle: POST_TITLE,
    ogDescription: POST_SUMMARY,
    ogType: "article",
    pathname: location.pathname,
    image: SOCIAL_IMAGE,
    extra: [
      { property: "article:published_time", content: PUBLISHED },
      { property: "article:modified_time", content: MODIFIED },
      {
        "script:ld+json": {
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: POST_TITLE,
          description: POST_SUMMARY,
          datePublished: PUBLISHED,
          dateModified: MODIFIED,
          image: SOCIAL_IMAGE,
          author: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
          publisher: {
            "@type": "Organization",
            name: SITE_NAME,
            url: SITE_URL,
            logo: {
              "@type": "ImageObject",
              url: `${SITE_URL}/brand/logo-full.svg`,
            },
          },
          mainEntityOfPage: { "@type": "WebPage", "@id": POST_URL },
        },
      },
    ],
  })

// Names and photos come from the shared author registry so a photo swap
// happens in one place; only the long-form bios are specific to this post.
const FOUNDERS = [
  {
    ...AUTHORS.torage,
    bio: "Tor Åge has spent nearly a decade building Unacast's engineering organization, serving as CTO and a member of the executive leadership team. Having led engineering teams across Europe and the United States, he now brings that experience to Pine59, where he is focused on building the next generation of scalable, AI-ready location intelligence products.",
  },
  {
    ...AUTHORS.thomas,
    bio: "Thomas is one of the original founders of Unacast. Over the past decade he has served as both CEO and President, leading the company's growth in the United States while driving innovation across the business as part of the executive leadership team. At Pine59, Thomas leads our US business and continues to focus on expanding our presence across North America.",
  },
  {
    ...AUTHORS.kjartan,
    bio: "Kjartan is also one of the original founders of Unacast. Over the past decade he led the company's European business and the Turbine product suite, serving as COO, General Manager and part of the executive leadership team. At Pine59, Kjartan leads our European business and focuses on expanding our presence across the Nordics, the UK and beyond.",
  },
]

// Shown when a visitor arrives from an old unacast.com blog post URL
// (?from-post=<slug>, set by routes/post.$.tsx and the legacy-host redirect
// in lib/unacast.ts). Explains the article is gone and offers two ways
// forward: read this page (closes the dialog) or head to the docs.
function LegacyPostDialog() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [dismissed, setDismissed] = useState(false)
  if (dismissed || !searchParams.has("from-post")) return null

  const close = () => {
    setDismissed(true)
    const next = new URLSearchParams(searchParams)
    next.delete("from-post")
    setSearchParams(next, { replace: true, preventScrollReset: true })
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-forest/70 px-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="legacy-post-title"
    >
      <div className="relative w-full max-w-lg rounded-lg bg-paper p-8 shadow-2xl sm:p-10">
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className="absolute right-4 top-4 rounded p-1 text-ink/50 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
            <path
              d="M12.6667 4.27337L11.7267 3.33337L8.00004 7.06004L4.27337 3.33337L3.33337 4.27337L7.06004 8.00004L3.33337 11.7267L4.27337 12.6667L8.00004 8.94004L11.7267 12.6667L12.6667 11.7267L8.94004 8.00004L12.6667 4.27337Z"
              fill="currentColor"
            />
          </svg>
        </button>
        <h2
          id="legacy-post-title"
          className="font-sans text-2xl font-semibold tracking-tight leading-snug text-ink"
        >
          Looking for a specific Unacast article?
        </h2>
        <p className="mt-2 font-sans text-lg font-medium text-ink">
          You&rsquo;ve come to the right place.
        </p>
        <p className="mt-4 text-[17px] leading-relaxed text-ink/75">
          Pine59 is the new company continuing Unacast&rsquo;s Aggregated
          Location Insights business. As part of that transition, some older
          Unacast articles have been retired and their links now bring you
          here.
        </p>
        <p className="mt-4 text-[17px] leading-relaxed text-ink/75">
          The original article is no longer available, but you can explore
          Pine59 and our latest guides below.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-4">
          <Button onClick={close}>Meet Pine59</Button>
          <ButtonLink
            variant="text"
            href="https://console.pine59.com/docs/guides"
          >
            Explore guides and examples
          </ButtonLink>
        </div>
      </div>
    </div>
  )
}

export default function WelcomeToPine59Post() {
  return (
    <BlogLayout post={post}>
      <LegacyPostDialog />

      <BlogProse>
        <WrittenBy post={post} />

        <P>
          Today we&rsquo;re excited to introduce Pine59 - the new home of
          Unacast&rsquo;s former Aggregated Location Insights business.
        </P>
        <P>
          For our existing customers, the most important message is simple:{" "}
          <strong className="font-semibold text-ink">
            Nothing changes about how you work with us.
          </strong>
        </P>
        <P>
          The products you rely on, the data you trust, your contracts,
          integrations and APIs all continue as before. The same people remain
          behind the platform. The difference is that we&rsquo;re now an
          independent company with one singular mission: To build the
          world&rsquo;s leading platform for aggregated location intelligence.
        </P>

        <BlogCta />

        <H2>A decade in the making</H2>
        <P>Pine59 didn&rsquo;t start today.</P>
        <P>
          It started more than a decade ago when we began asking a simple
          question: Can we understand how the physical world changes - not by
          tracking individuals, but by measuring places and populations?
        </P>
        <P>
          That question became one of the foundations of Unacast&rsquo;s
          Aggregated Location Insights business.
        </P>
        <P>
          Over the years we&rsquo;ve helped retailers understand store
          performance, cities measure mobility, real estate developers evaluate
          locations, infrastructure planners optimize transportation, and
          technology companies build entirely new products powered by
          aggregated location intelligence.
        </P>
        <P>
          Today, after the completion of a management buyout led by members of
          Unacast&rsquo;s leadership team who have spent years building this
          business, that business enters its next chapter, as Pine59.
        </P>
      </BlogProse>

      <div className="mt-14">
        <DocsBanner />
      </div>

      <BlogProse>
        <H2>So, what is aggregated location intelligence?</H2>
        <P>
          Every day, all of us move through cities, visit stores, commute to
          work, travel between neighborhoods and spend time in parks, airports
          and shopping districts. Together, these movements create patterns
          that reveal how places and populations change and perform.
        </P>
        <P>
          Aggregated location intelligence transforms those collective patterns
          into actionable intelligence.
        </P>
        <P>
          Rather than looking at individual behavior, Pine59 measures how
          places and populations interact. The location signals we receive are
          already aggregated at the source before they ever reach Pine59,
          ensuring our datasets reveal collective patterns - not individual
          behavior.
        </P>
        <P>
          The result is aggregated location intelligence that helps organizations
          understand where activity is growing, how neighborhoods are evolving,
          how infrastructure is being used and how movement changes over time -
          powering better decisions across retail, real estate, finance, urban
          planning, transportation and AI.
        </P>
        <Quote>
          Designed to understand places and populations — not individual
          people.
        </Quote>

        <H2>Why the name Pine59?</H2>
        <P>The name reflects both where we came from and how we think.</P>
        <P>
          <strong className="font-semibold text-ink">Pine</strong> comes from
          the pine forests surrounding the cabin where the idea behind the
          company was born.
        </P>
        <P>
          <strong className="font-semibold text-ink">59</strong> refers to the
          latitude of origin, 59° North, where Norway&rsquo;s combination of
          nature, engineering and curiosity has shaped how we approach building
          products.
        </P>
        <P>
          While our roots are Norwegian, our ambition has always been global.
        </P>

        <H2>What we build</H2>
        <P>
          Pine59 provides aggregated location intelligence built around
          collective patterns rather than individual behavior. We transform
          aggregated location statistics into actionable insights about:
        </P>
        <List
          items={[
            "Places",
            "Neighborhoods",
            "Cities",
            "Regions",
            "Infrastructure",
          ]}
        />

        <P>
          Our products help customers understand visitation, movement, trade
          areas, dwell time, mobility and behavioral change through analytics,
          applications and AI.
        </P>
        <P>
          Everything we build starts from the same philosophy:{" "}
          <em className="font-medium not-italic text-ink">
            Designed to understand places and populations - not individual
            people.
          </em>
        </P>
      </BlogProse>

      <div className="mt-14">
        <DocsBanner
          image={{
            src: "/product/insights-banner.webp",
            srcSet:
              "/product/insights-banner-800.webp 800w, /product/insights-banner-1400.webp 1400w, /product/insights-banner.webp 2000w",
            sizes: "(min-width: 1024px) 50vw, 100vw",
            alt: "Pine59 Insights showing a Dunkin' location's foot traffic trend, rank and visitor stats",
            width: 2000,
            height: 679,
          }}
          body="Pine59 Insights: a guided, visual platform for decision making. Foot traffic trends, visitor profiles and trade areas for your locations, no code required."
          ctaLabel={null}
          imageRing=""
          imageFade={false}
        />
      </div>

      <BlogProse>
        <H2>Who we build for</H2>
        <P>
          Pine59 builds for organizations that make costly, hard-to-reverse
          decisions about physical space and need one reliable answer: how do
          people actually move through and use places?
        </P>
        <P>
          Our core buyers are enterprises building products on top of our
          data, retailers and real estate investors choosing sites and
          underwriting deals, consulting firms enriching their own analysis,
          and telcos like BT and Telia that use our platform and technology to
          turn signals into insights. Whether they&rsquo;re embedding our
          metrics into a platform or making a capital decision, they all need
          the same thing - a reliable, privacy-safe understanding on how
          people move through and use places.
        </P>

        <H2>What comes next</H2>
        <P>
          Our ambition is straightforward. We want Pine59 to become the leading
          platform for aggregated location intelligence.
        </P>
        <P>That means:</P>
        <List
          items={[
            "expanding geographic coverage",
            "investing heavily in AI-ready datasets",
            "making our APIs and developer platform even stronger",
            "launching new products built around statistical insights",
            "continuing to deliver the reliability our customers depend on every day",
          ]}
        />
        <P>
          We&rsquo;ll move faster. We&rsquo;ll focus deeper. And we&rsquo;ll
          keep building for the organizations that rely on understanding how
          the physical world changes.
        </P>

        <H2>Meet the founders</H2>
        <P>
          Pine59 was created through a management buyout led by two Unacast
          co-founders and the company&rsquo;s former CTO. Together, we&rsquo;ve
          spent more than a decade building Unacast&rsquo;s Aggregated Location
          Insights business. Today, we&rsquo;re bringing that experience to
          Pine59 with one clear focus: building the world&rsquo;s leading
          platform for aggregated location intelligence.
        </P>
        <div className="mt-8 divide-y divide-line">
          {FOUNDERS.map((f) => (
            <div
              key={f.name}
              className="flex items-start gap-4 py-8 first:pt-0 last:pb-0"
            >
              <img
                src={f.avatar}
                alt=""
                className="h-14 w-14 shrink-0 rounded-full object-cover"
              />
              <div>
                <h3 className="font-sans text-lg font-semibold text-ink">
                  {f.name}
                </h3>
                <P>{f.bio}</P>
              </div>
            </div>
          ))}
        </div>

        <H2>The next chapter starts now</H2>
        <P>
          To our existing customers: Thank you for trusting us over the years.
        </P>
        <P>To everyone discovering Pine59 for the first time: Welcome.</P>
        <P>We&rsquo;re just getting started.</P>

        <P>Best regards from</P>
        <div className="mt-3 flex items-center gap-3">
          <span className="flex -space-x-2">
            {FOUNDERS.map((f) => (
              <img
                key={f.name}
                src={f.avatar}
                alt={f.name}
                title={f.name}
                className="h-9 w-9 rounded-full object-cover ring-2 ring-paper"
              />
            ))}
          </span>
          <span className="text-[18px] leading-relaxed text-ink/75">
            and the whole team here at Pine59
          </span>
        </div>
      </BlogProse>
    </BlogLayout>
  )
}
